/**
 * Editable Room Node Component
 * 
 * Draggable and resizable room shape for Edit Mode
 * Date: 09-10-2025 12:40:09
 * 
 * @module floor-plan-editor/EditableRoomNode
 */

import React, { useRef, useEffect } from 'react';
import { Group, Rect, Text, Transformer, Konva } from '../../../lib/konva-optimized';
import type { KonvaEventObject } from 'konva/lib/Node';

/**
 * Room data for rendering
 */
interface RoomData {
  id: string;
  number: string;
  name: string;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  hasOverlap?: boolean;
}

/**
 * Editable room node props
 */
interface EditableRoomNodeProps {
  room: RoomData;
  isEditMode: boolean;
  isSelected: boolean;
  hasOverlap: boolean;
  onSelect: (roomId: string) => void;
  onDragStart?: () => void;
  onDragEnd: (roomId: string, e: KonvaEventObject<DragEvent>) => void;
  onTransformEnd: (roomId: string, e: KonvaEventObject<Event>) => void;
  dragBoundFunc?: (pos: { x: number; y: number }) => { x: number; y: number };
}

/**
 * Editable Room Node Component
 * 
 * Renders a room as a draggable/resizable shape on Konva canvas
 * Shows resize handles when selected in Edit Mode
 * 
 * @param props - Component props
 * @returns React component
 */
export const EditableRoomNode: React.FC<EditableRoomNodeProps> = ({
  room,
  isEditMode,
  isSelected,
  hasOverlap,
  onSelect,
  onDragStart,
  onDragEnd,
  onTransformEnd,
  dragBoundFunc
}) => {
  const shapeRef = useRef<Konva.Group>(null);
  const transformerRef = useRef<Konva.Transformer>(null);
  const isDraggingRef = useRef(false);
  const isTransformingRef = useRef(false);
  const lastDragUpdateRef = useRef(0);

  /**
   * Attach transformer to selected room
   */
  useEffect(() => {
    if (isSelected && isEditMode && transformerRef.current && shapeRef.current) {
      // Attach transformer to the shape
      transformerRef.current.nodes([shapeRef.current]);
      transformerRef.current.getLayer()?.batchDraw();
    }
  }, [isSelected, isEditMode]);

  /**
   * Shape caching for performance optimization
   * Disabled during drag/transform operations for smooth movement
   * Uses pixelRatio for sharp rendering on retina displays
   * @created 17-10-2025 07:25:00
   */
  useEffect(() => {
    if (!shapeRef.current || isDraggingRef.current || isTransformingRef.current) return;
    
    // Cache immediately with retina support
    const timer = setTimeout(() => {
      if (shapeRef.current && !shapeRef.current.isCached()) {
        shapeRef.current.cache({
          pixelRatio: window.devicePixelRatio || 2
        });
        shapeRef.current.getLayer()?.batchDraw();
      }
    }, 16);  // One frame delay (60fps)
    
    return () => clearTimeout(timer);
  }, []);

  /**
   * Clear cache when transform starts to prevent visual artifacts
   * This fixes the "duplicate box with gaps" issue during resizing
   * @created 17-10-2025 07:30:00
   */
  useEffect(() => {
    if (isTransformingRef.current && shapeRef.current?.isCached()) {
      shapeRef.current.clearCache();
      shapeRef.current.getLayer()?.batchDraw();
    }
  }, [isTransformingRef.current]);

  /**
   * Re-cache when room properties change (but not during interactions)
   */
  useEffect(() => {
    if (!shapeRef.current || isDraggingRef.current || isTransformingRef.current) return;
    
    if (shapeRef.current.isCached()) {
      shapeRef.current.clearCache();
      shapeRef.current.cache({
        pixelRatio: window.devicePixelRatio || 2
      });
      shapeRef.current.getLayer()?.batchDraw();
    }
  }, [room.number, room.name, room.position.width, room.position.height, hasOverlap]);

  /**
   * Calculate border color based on state
   */
  const getBorderColor = (): string => {
    if (hasOverlap) return '#DC2626'; // Red for overlap
    if (isSelected) return '#2563EB'; // Blue for selected
    return '#94A3B8'; // Gray for normal
  };

  /**
   * Calculate border width based on state
   */
  const getBorderWidth = (): number => {
    if (hasOverlap) return 3;
    if (isSelected) return 3;
    return 1;
  };

  return (
    <>
      <Group
        ref={shapeRef}
        x={room.position.x}
        y={room.position.y}
        draggable={isEditMode}
        perfectDrawEnabled={false}
        onClick={() => isEditMode && onSelect(room.id)}
        onTap={() => isEditMode && onSelect(room.id)}
        onMouseEnter={() => {
          if (!isEditMode) return;
          const stage = shapeRef.current?.getStage();
          if (stage) stage.container().style.cursor = 'move';
        }}
        onMouseLeave={() => {
          const stage = shapeRef.current?.getStage();
          if (stage) stage.container().style.cursor = 'default';
        }}
        onDragStart={() => {
          isDraggingRef.current = true;
          // Clear cache during drag for smooth movement
          if (shapeRef.current?.isCached()) {
            shapeRef.current.clearCache();
          }
          // Change cursor to grabbing
          const stage = shapeRef.current?.getStage();
          if (stage) stage.container().style.cursor = 'grabbing';
          onDragStart?.();
        }}
        onDragMove={() => {
          // Throttled visual feedback during drag for better performance
          // Use timestamp-based throttling to avoid excessive redraws
          if (!shapeRef.current) return;
          
          const now = performance.now();
          if (now - lastDragUpdateRef.current > 50) { // 20fps max
            const rectNode = shapeRef.current.findOne('Rect') as any;
            if (rectNode && rectNode.strokeWidth) {
              rectNode.strokeWidth(3);
              shapeRef.current.getLayer()?.batchDraw();
            }
            lastDragUpdateRef.current = now;
          }
        }}
        onDragEnd={(e) => {
          isDraggingRef.current = false;
          onDragEnd(room.id, e);
          // Reset cursor
          const stage = shapeRef.current?.getStage();
          if (stage) stage.container().style.cursor = 'default';
          // Re-cache after one frame
          setTimeout(() => {
            if (shapeRef.current) {
              shapeRef.current.cache({
                pixelRatio: window.devicePixelRatio || 2
              });
              shapeRef.current.getLayer()?.batchDraw();
            }
          }, 16);
        }}
        onTransformEnd={(e) => onTransformEnd(room.id, e)}
        dragBoundFunc={dragBoundFunc}
      >
        {/* Room rectangle */}
        <Rect
          width={room.position.width}
          height={room.position.height}
          fill="#FFFFFF"
          stroke={getBorderColor()}
          strokeWidth={getBorderWidth()}
          shadowColor={isSelected ? '#2563EB' : '#000000'}
          shadowBlur={isSelected ? 10 : 0}
          shadowOpacity={isSelected ? 0.3 : 0}
          cornerRadius={4}
          perfectDrawEnabled={false}
        />

        {/* Room number label */}
        <Text
          x={0}
          y={room.position.height / 2 - 12}
          width={room.position.width}
          text={room.number}
          fontSize={14}
          fontStyle="bold"
          fill="#1E293B"
          align="center"
          verticalAlign="middle"
          perfectDrawEnabled={false}
        />

        {/* Room name label */}
        <Text
          x={0}
          y={room.position.height / 2 + 4}
          width={room.position.width}
          text={room.name}
          fontSize={11}
          fill="#64748B"
          align="center"
          verticalAlign="middle"
          ellipsis={true}
          perfectDrawEnabled={false}
        />

        {/* Overlap indicator (X icon) */}
        {hasOverlap && (
          <>
            <Rect
              x={room.position.width - 24}
              y={4}
              width={20}
              height={20}
              fill="#DC2626"
              cornerRadius={10}
              perfectDrawEnabled={false}
            />
            <Text
              x={room.position.width - 24}
              y={4}
              width={20}
              height={20}
              text="✕"
              fontSize={14}
              fill="#FFFFFF"
              align="center"
              verticalAlign="middle"
              perfectDrawEnabled={false}
            />
          </>
        )}
      </Group>

      {/* Transformer for resize handles (only when selected in Edit Mode) */}
      {isSelected && isEditMode && (
        <Transformer
          ref={transformerRef}
          keepRatio={false}
          centeredScaling={false}
          ignoreStroke={true}
          rotateEnabled={false}
          enabledAnchors={['top-left', 'top-right', 'bottom-left', 'bottom-right']}
          boundBoxFunc={(oldBox, newBox) => {
            // ONLY enforce min/max here (fast rejection checks)
            if (newBox.width < 50 || newBox.height < 50) return oldBox;
            if (newBox.width > 500 || newBox.height > 500) return oldBox;
            return newBox;
          }}
          onTransform={(e) => {
            // Grid snapping HERE (called ~60x/sec, not 1000+)
            const node = e.target as any;
            const scaleX = node.scaleX();
            const scaleY = node.scaleY();
            
            const gridSize = 20;
            const snappedWidth = Math.round((node.width() * scaleX) / gridSize) * gridSize;
            const snappedHeight = Math.round((node.height() * scaleY) / gridSize) * gridSize;
            
            // Apply snapped dimensions
            node.width(snappedWidth / scaleX);
            node.height(snappedHeight / scaleY);
          }}
                  onTransformStart={() => {
                    isTransformingRef.current = true;
                    // Clear cache immediately to prevent visual artifacts
                    if (shapeRef.current?.isCached()) {
                      shapeRef.current.clearCache();
                      shapeRef.current.getLayer()?.batchDraw();
                    }
                  }}
                  onTransformEnd={() => {
                    isTransformingRef.current = false;
                    // Re-cache after transform completes with a delay
                    setTimeout(() => {
                      if (shapeRef.current && !isTransformingRef.current && !isDraggingRef.current) {
                        shapeRef.current.cache({
                          pixelRatio: window.devicePixelRatio || 2
                        });
                        shapeRef.current.getLayer()?.batchDraw();
                      }
                    }, 50); // Longer delay to ensure transform is fully complete
                  }}
          borderStroke="#2563EB"
          borderStrokeWidth={3}
          borderDash={[8, 4]}
          anchorFill="#2563EB"
          anchorStroke="#FFFFFF"
          anchorStrokeWidth={4}
          anchorSize={24}
          anchorCornerRadius={12}
        />
      )}
    </>
  );
};

